import React, { useState, useEffect } from 'react';
import {
  useGetUsersQuery,
  useDeleteUserMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetUserQuery
} from '../redux/services/usersApi';
import { useGetAccountsQuery } from '../redux/services/accountsApi';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormHelperText,
  Switch,
  FormControlLabel,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

// Local storage key for filters
const STORAGE_KEY = 'userFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
      page: 1,
      limit: 25,
      search: '',
      block_user: 0,
      status: 0,
      role_id: -1,
    };
  } catch (e) {
    return {
      page: 1,
      limit: 25,
      search: '',
      block_user: 0,
      status: 0,
      role_id: -1,
    };
  }
};

// User Form Dialog Component
const UserFormDialog = ({ open, handleClose, userId, onSuccess }) => {
  const isEditMode = !!userId;
  const { user: currentUser } = useAuth();
  const isCurrentUser = currentUser?.id === userId;
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_id: 1,
    status: 1,
    block_user: false,
    accounts: '',
  });
  const [errors, setErrors] = useState({});
  const { showToast } = useToast();
  
  // Queries and mutations
  const { data: userData, isLoading: isLoadingUser } = useGetUserQuery(userId, {
    skip: !isEditMode || !open
  });
  const { data: accountsData } = useGetAccountsQuery({ limit: 1000 });
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  
  const isProcessing = isCreating || isUpdating;
  
  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role_id: 1,
        status: 1,
        block_user: false,
        accounts: '',
      });
      setErrors({});
    }
  }, [open]);
  
  // Set form data when editing
  useEffect(() => {
    if (isEditMode && userData) {
      console.log('User data received:', userData);
      
      // Handle different possible response structures
      const user = userData.user || userData.data || userData;
      
      if (!user) {
        console.error('No user data found in response:', userData);
        return;
      }

      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        password: '',
        role_id: user.role_id || 1,
        status: user.status || 1,
        block_user: Boolean(user.block_user),
        accounts: user.accounts || '',
      });
    }
  }, [isEditMode, userData]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleAccountsChange = (event) => {
    const selectedAccounts = event.target.value;
    const accountIds = selectedAccounts.map(id => id.toString()).join(',');
    setFormData((prev) => ({
      ...prev,
      accounts: accountIds,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.role_id) {
      newErrors.role_id = 'Role is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        role_id: parseInt(formData.role_id, 10),
      };

      if (isEditMode) {
        await updateUser({ userId, ...submitData }).unwrap();
        showToast('User updated successfully', 'success');
      } else {
        await createUser(submitData).unwrap();
        showToast('User created successfully', 'success');
      }
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error saving user:', error);
      showToast(error.data?.message || 'Error saving user', 'error');
    }
  };
  
  if (isEditMode && isLoadingUser) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.first_name}>
                <TextField
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  disabled={isProcessing || isCurrentUser}
                  fullWidth
                  required
                  error={!!errors.first_name}
                />
                {errors.first_name && (
                  <FormHelperText>{errors.first_name}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.last_name}>
                <TextField
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  disabled={isProcessing || isCurrentUser}
                  fullWidth
                  required
                  error={!!errors.last_name}
                />
                {errors.last_name && (
                  <FormHelperText>{errors.last_name}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.email}>
                <TextField
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isProcessing || isCurrentUser}
                  fullWidth
                  required
                  error={!!errors.email}
                />
                {errors.email && (
                  <FormHelperText>{errors.email}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.password}>
                <TextField
                  label={isEditMode ? "New Password (leave blank to keep current)" : "Password"}
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isProcessing || isCurrentUser}
                  fullWidth
                  required={!isEditMode}
                  error={!!errors.password}
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: "new-password"
                  }}
                />
                {errors.password && (
                  <FormHelperText>{errors.password}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.role_id}>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role_id"
                  value={formData.role_id}
                  label="Role"
                  onChange={handleInputChange}
                  disabled={isProcessing || isCurrentUser}
                >
                  <MenuItem value={1}>User</MenuItem>
                  <MenuItem value={2}>Admin</MenuItem>
                </Select>
                {errors.role_id && (
                  <FormHelperText>{errors.role_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange}
                  disabled={isProcessing || isCurrentUser}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={2}>Not Active</MenuItem>
                </Select>
                {errors.status && (
                  <FormHelperText>{errors.status}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="block_user"
                    checked={formData.block_user}
                    onChange={handleInputChange}
                    disabled={isProcessing || isCurrentUser}
                  />
                }
                label="Block User"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.accounts}>
                <InputLabel>Accounts</InputLabel>
                <Select
                  multiple
                  value={formData.accounts ? formData.accounts.split(',').map(id => parseInt(id, 10)) : []}
                  onChange={handleAccountsChange}
                  label="Accounts"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const account = accountsData?.data?.find(acc => acc.id === value);
                        return (
                          <Chip
                            key={value}
                            label={account?.name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {accountsData?.data?.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.accounts && (
                  <FormHelperText>{errors.accounts}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained" 
            color="primary"
            disabled={isProcessing || isCurrentUser}
          >
            {isProcessing ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Users = () => {
  // Replace navigate with state for our dialog
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();
  
  // Initialize state from localStorage
  const initialFilters = getSavedFilters();
  const [filters, setFilters] = useState(initialFilters);
  // Add state for search input value
  const [searchInput, setSearchInput] = useState(initialFilters.search);
  
  // Query with all filters
  const { data: users, isLoading, error } = useGetUsersQuery(filters);
  const { data: accountsData } = useGetAccountsQuery({ limit: 1000 });
  const [deleteUser] = useDeleteUserMutation();

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => {
      const updatedFilters = {
        ...prev,
        [name]: value
      };
      
      // Only reset page when changing filters other than page itself
      if (name !== 'page') {
        updatedFilters.page = 1;
      }
      
      return updatedFilters;
    });
  };

  // Add function to handle search execution
  const handleSearch = () => {
    handleFilterChange('search', searchInput);
  };

  // Add function to clear all filters
  const clearFilters = () => {
    const defaultFilters = {
      page: 1,
      limit: 25,
      search: '',
      block_user: 0,
      status: 0,
      role_id: -1,
    };
    setFilters(defaultFilters);
    setSearchInput('');
  };

  const handleChangePage = (event, newPage) => {
    handleFilterChange('page', newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    handleFilterChange('limit', parseInt(event.target.value, 10));
  };

  const handleDelete = async (userId) => {
    if (currentUser?.id === userId) {
      showToast('You cannot delete your own profile.', 'warning');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId).unwrap();
        showToast('User deleted successfully', 'success');
      } catch (err) {
        showToast('Failed to delete user', 'error');
      }
    }
  };
  
  // New handlers for the form dialog
  const handleOpenCreateDialog = () => {
    setSelectedUserId(null);
    setFormDialogOpen(true);
  };
  
  const handleOpenEditDialog = (userId) => {
    if (currentUser?.id === userId) {
      showToast('You cannot edit your own profile here. Please use the profile settings page.', 'warning');
      return;
    }
    setSelectedUserId(userId);
    setFormDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setFormDialogOpen(false);
  };

  // Role and status mapping
  const roleNames = {
    1: 'User',
    2: 'Admin'
  };
  
  const statusLabels = {
    1: 'Active',
    2: 'Not Active'
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading users
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Users</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCreateDialog}
        >
          Add New User
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            sx={{ flexGrow: 1 }}
            label="Search"
            variant="outlined"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={filters.role_id}
              label="Role"
              onChange={(e) => handleFilterChange('role_id', e.target.value)}
            >
              <MenuItem value={-1}>All</MenuItem>
              <MenuItem value={1}>User</MenuItem>
              <MenuItem value={2}>Admin</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Accounts</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Blocked</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.items?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{roleNames[user.role_id] || `Unknown (${user.role_id})`}</TableCell>
                <TableCell>
                  {user.accounts ? (
                    user.accounts.split(',').map((accountId) => {
                      const account = accountsData?.data?.find(acc => acc.id === parseInt(accountId, 10));
                      return account ? (
                        <Chip
                          key={accountId}
                          label={account.name}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ) : null;
                    })
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{statusLabels[user.status] || `Unknown (${user.status})`}</TableCell>
                <TableCell>{user.block_user ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <IconButton 
                    onClick={() => handleOpenEditDialog(user.id)}
                    disabled={currentUser?.id === user.id}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(user.id)}
                    disabled={currentUser?.id === user.id}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={users?.total || 0}
          page={(filters.page - 1)}
          rowsPerPage={filters.limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      
      {/* User Form Dialog */}
      <UserFormDialog 
        open={formDialogOpen}
        handleClose={handleCloseDialog}
        userId={selectedUserId}
        onSuccess={() => {}} // We don't need to do anything here since RTK Query will handle the cache invalidation
      />
    </Box>
  );
};

export default Users; 